import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { Montant as MontantCentimes } from '@artosera/shared';
import styles from './commun.module.css';
import { useFormats } from './formats.js';

/**
 * Un montant en centimes, écrit en euros. `null` n'est jamais « 0 € » : c'est
 * « à saisir », en retrait, et la ligne n'entre dans aucun total.
 */
export function Montant({ centimes }: { centimes: MontantCentimes }): JSX.Element {
  const { t } = useTranslation('console');
  const { euros } = useFormats();
  if (centimes === null) return <span className={styles.aSaisir}>{t('vueDAigle.aSaisir')}</span>;
  return <>{euros(centimes)}</>;
}

/** « N lignes à saisir ne sont pas comptées » : rien quand le compteur est nul. */
export function NonComptees({ nombre }: { nombre: number }): JSX.Element | null {
  const { t } = useTranslation('console');
  if (nombre <= 0) return null;
  return <p className={styles.nonComptees}>{t('vueDAigle.nonComptees', { count: nombre })}</p>;
}

/**
 * Un lien interne vers un écran de la console. Une adresse qui ne commence pas
 * par `/` (ou qui commence par `//`) n'est pas une route du front : rien n'est
 * rendu plutôt qu'un lien vers l'extérieur.
 */
export function LienConsole({
  vers,
  bouton = false,
  children,
}: {
  vers: string;
  bouton?: boolean;
  children: ReactNode;
}): JSX.Element | null {
  if (!vers.startsWith('/') || vers.startsWith('//')) return null;
  return (
    <Link to={vers} className={bouton ? styles.lienBouton : styles.lien}>
      {children}
    </Link>
  );
}

/**
 * Un panneau numéroté de la page : le numéro, le titre, une note et des
 * actions en tête, puis le contenu. Le titre est un `h2` relié au panneau.
 */
export function Section({
  id,
  numero,
  titre,
  note,
  actions,
  pastille,
  className,
  children,
}: {
  id: string;
  numero: string;
  titre: string;
  note?: ReactNode;
  actions?: ReactNode;
  pastille?: ReactNode;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  const idTitre = `titre-${id}`;
  return (
    <section
      id={id}
      className={[styles.panneau, className].filter(Boolean).join(' ')}
      aria-labelledby={idTitre}
    >
      <div className={styles.sectionTete}>
        <div className={styles.sectionTitre}>
          <span className={styles.numero} aria-hidden="true">
            {numero}
          </span>
          <h2 id={idTitre}>{titre}</h2>
          {pastille}
        </div>
        {note !== undefined || actions !== undefined ? (
          <div className={styles.sectionActions}>
            {note !== undefined ? <span className={styles.note}>{note}</span> : null}
            {actions}
          </div>
        ) : null}
      </div>
      {children}
    </section>
  );
}
